"use client";

import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconAlertInfoOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { PUBLIC_RPC } from "@/lib/chains";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useEffect, useState } from "react";
import type { EventAbi } from "./eventAbiUtils";

/** Map explorer hostnames to chain IDs */
const EXPLORER_CHAIN_MAP: Record<string, number> = {
  "etherscan.io": 1,
  "basescan.org": 8453,
  "arbiscan.io": 42161,
  "optimistic.etherscan.io": 10,
  "polygonscan.com": 137,
  "bscscan.com": 56,
  "snowtrace.io": 43114,
  "ftmscan.com": 250,
  "cronoscan.com": 25,
  "lineascan.build": 59144,
  "era.zksync.network": 324,
  "scrollscan.com": 534352,
  "mantlescan.xyz": 5000,
  "explorer.mode.network": 34443,
  "explorer.b3.fun": 8333,
  "sepolia.etherscan.io": 11155111,
  "sepolia.basescan.org": 84532,
  "sepolia-explorer.b3.fun": 1993
};
const TX_HASH_RE = /^0x[0-9a-fA-F]{64}$/;

/**
 * Parse a transaction URL or hash into { txHash, chainId }.
 * Supports raw tx hashes (needs fallback chainId) and explorer URLs.
 */
function parseTxInput(input: string, fallbackChainId?: number): {
  txHash: string;
  chainId: number;
} | {
  error: string;
} {
  const trimmed = input.trim();
  if (TX_HASH_RE.test(trimmed)) {
    if (!fallbackChainId) {
      return {
        error: "Paste a full explorer URL or configure chainId on the trigger"
      };
    }
    return {
      txHash: trimmed,
      chainId: fallbackChainId
    };
  }
  try {
    const url = new URL(trimmed);
    const host = url.hostname.replace(/^www\./, "");
    const chainId = EXPLORER_CHAIN_MAP[host];
    if (!chainId) return {
      error: `Unsupported explorer: ${host}`
    };
    const txMatch = url.pathname.match(/\/tx\/(0x[0-9a-fA-F]{64})/);
    if (!txMatch) return {
      error: "Could not find transaction hash in URL"
    };
    return {
      txHash: txMatch[1],
      chainId
    };
  } catch {
    return {
      error: "Enter a valid transaction URL or hash"
    };
  }
}
export type { EventAbi } from "./eventAbiUtils";
export interface TxLogResult {
  data: Record<string, unknown>;
  label: string;
}
interface TxLogFetcherProps {
  /** Chain ID from the trigger payload (fallback for raw tx hashes) */
  chainId?: number;
  /** Event name to filter matching logs */
  eventName?: string;
  /** Full event ABI for decoding logs directly from chain */
  eventAbi?: EventAbi;
  /** Callback when user selects a parsed log entry (null clears selection) */
  onRun: (payload: Record<string, unknown> | null) => void;
}

/**
 * Component for fetching and selecting event logs from a transaction.
 * Fetches tx receipt directly from the chain via viem — no backend needed.
 */
export default function TxLogFetcher({
  chainId,
  eventName,
  eventAbi,
  onRun
}: TxLogFetcherProps) {
  const [txInput, setTxInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<TxLogResult[] | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<string>("0");
  const [txMeta, setTxMeta] = useState<{
    hash: string;
    chainId: number;
    status: string;
  } | null>(null);
  const handleFetch = useCallback(async () => {
    const parsed = parseTxInput(txInput, chainId);
    if ("error" in parsed) {
      setError(parsed.error);
      onRun(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    setResults(null);
    setTxMeta(null);
    onRun(null);
    try {
      const [{
        createPublicClient,
        http,
        decodeEventLog
      }, viemChains] = await Promise.all([import("viem"), import("viem/chains")]);
      const chain = Object.values(viemChains).find((c: unknown) => {
        const candidate = c as {
          id?: number;
        };
        return candidate.id === parsed.chainId;
      }) as (typeof viemChains)["mainnet"] | undefined;
      if (!chain) throw new Error(`Unsupported chain: ${parsed.chainId}`);
      const rpcUrl = PUBLIC_RPC[parsed.chainId] ?? chain.rpcUrls.default.http[0];
      if (!rpcUrl) throw new Error(`No RPC URL for chain ${parsed.chainId}`);
      const client = createPublicClient({
        chain,
        transport: http(rpcUrl)
      });
      const receipt = await client.getTransactionReceipt({
        hash: parsed.txHash as `0x${string}`
      });
      setTxMeta({
        hash: parsed.txHash,
        chainId: parsed.chainId,
        status: receipt.status === "success" ? "success" : "reverted"
      });
      if (!receipt.logs || receipt.logs.length === 0) {
        setError("No events found in this transaction");
        return;
      }

      // Decode logs using the event ABI
      const decoded: TxLogResult[] = [];
      const allEventNames = new Set<string>();
      for (const log of receipt.logs) {
        if (!eventAbi) {
          // No ABI — show raw logs
          decoded.push({
            label: `Log #${log.logIndex}`,
            data: {
              params: {},
              raw: {
                address: log.address,
                blockNumber: Number(log.blockNumber),
                transactionHash: parsed.txHash,
                logIndex: log.logIndex,
                topics: log.topics,
                data: log.data
              }
            }
          });
          continue;
        }
        try {
          const result = decodeEventLog({
            abi: [eventAbi],
            data: log.data,
            topics: log.topics
          });
          const decodedName = String(result.eventName ?? "");
          allEventNames.add(decodedName);

          // Filter by event name if specified
          if (eventName && decodedName !== eventName) continue;
          const params: Record<string, unknown> = {};
          const args = result.args as unknown;
          if (args && typeof args === "object" && !Array.isArray(args)) {
            for (const [key, value] of Object.entries(args as Record<string, unknown>)) {
              params[key] = typeof value === "bigint" ? value.toString() : value;
            }
          }
          decoded.push({
            label: `${decodedName} (log #${log.logIndex})`,
            data: {
              params,
              raw: {
                address: log.address,
                blockNumber: Number(log.blockNumber),
                transactionHash: parsed.txHash,
                logIndex: log.logIndex,
                topics: log.topics,
                data: log.data
              }
            }
          });
        } catch {
          // Log doesn't match this ABI — try to identify it for error message
          if (log.topics[0]) allEventNames.add(`unknown(${log.topics[0].slice(0, 10)}...)`);
        }
      }
      if (decoded.length === 0 && eventName) {
        const found = [...allEventNames].filter(n => !n.startsWith("unknown")).join(", ");
        setError(`No "${eventName}" events found.${found ? ` Transaction has: ${found}` : ""}`);
        return;
      }
      if (decoded.length === 0) {
        setError("No decodable events found in this transaction");
        return;
      }
      setResults(decoded);
      setSelectedIndex("0");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch transaction");
    } finally {
      setIsLoading(false);
    }
  }, [txInput, chainId, eventName, eventAbi, onRun]);

  // Notify parent when selection changes or results load
  useEffect(() => {
    if (!results || results.length === 0) return;
    const idx = Math.min(Number(selectedIndex), results.length - 1);
    const payload = results[idx]?.data;
    if (payload) onRun(payload);
  }, [selectedIndex, results, onRun]);
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && txInput.trim()) {
      handleFetch();
    }
  }, [handleFetch, txInput]);
  return <div className="flex flex-1 flex-col gap-2 py-2">
      <div className="flex">
        <Input type="text" value={txInput} onChange={e_0 => setTxInput(e_0.target.value)} onKeyDown={handleKeyDown} placeholder="Paste tx hash or explorer URL..." className="text-ds-text-primary min-w-0 flex-1 dark:text-zinc-100" />
        <Button size="sm" onClick={handleFetch} disabled={isLoading || !txInput.trim()} className="shrink-0 !px-3">
          {isLoading ? <PixelLoader size="xs" /> : <IconMagnifyingGlass className="h-3.5 w-3.5" />}
        </Button>
      </div>
      {error && <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 dark:bg-red-900/20">
          <IconAlertInfoOutline18 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500 dark:text-red-400" />
          <span className="text-xs text-red-700 dark:text-red-300">{error}</span>
        </div>}
      {txMeta && <div className="label-2xs flex items-center gap-2 px-1">
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${txMeta.status === "success" ? "bg-green-500" : "bg-red-500"}`} />
          <span className="font-mono">
            {txMeta.hash.slice(0, 10)}...{txMeta.hash.slice(-8)}
          </span>
          <span>chain {txMeta.chainId}</span>
        </div>}
      {results && results.length > 0 && <RadioGroup value={selectedIndex} onValueChange={setSelectedIndex} className="gap-0">
          <div className="divide-ds-neutral-100 dark:divide-ds-neutral-800 divide-y">
            {results.map((result_0, i) => {
          const value_0 = String(i);
          const selected = selectedIndex === value_0;
          return <button key={i} type="button" onClick={() => setSelectedIndex(value_0)} className="flex w-full items-center gap-2.5 py-2 text-left">
                  <RadioGroupItem value={value_0} checked={selected} className="flex-shrink-0" />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-ds-text-secondary text-xs font-medium">{result_0.label}</span>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                      {Object.entries(result_0.data.params as Record<string, string> || {}).slice(0, 3).map(([k, v]) => <span key={k} className="label-2xs truncate normal-case tracking-normal">
                            <span className="font-medium uppercase tracking-wider">{k}:</span>{" "}
                            {typeof v === "string" && v.length > 20 ? `${v.slice(0, 10)}...${v.slice(-6)}` : String(v)}
                          </span>)}
                    </div>
                  </div>
                </button>;
        })}
          </div>
        </RadioGroup>}
    </div>;
}